(() => {
  const page = document.querySelector(".template-page-creator-program [data-creator-program-page]");
  const configNode = document.querySelector("[data-cp-config]");

  if (!page || !configNode) return;

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const mobileViewport = window.matchMedia("(max-width: 749px)");
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const liteMode = Boolean(connection?.saveData);

  document.documentElement.classList.add("cp-js");
  document.documentElement.classList.toggle("cp-lite", liteMode);

  const asBoolean = (value, fallback = true) => {
    if (value === "true") return true;
    if (value === "false") return false;
    return fallback;
  };

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const mix = (from, to, progress) => from + (to - from) * progress;

  let sections = [];
  let geometry = [];
  let frame = 0;
  let dirty = true;
  let recacheRequested = true;
  let galleries = [];
  let benefitCardPulses = [];
  let formReadyTimers = new WeakMap();
  let resizeObserver;

  const motionSettings = () => {
    const strength = configNode.dataset.strength || "standard";
    const ranges = {
      subtle: { desktopPrevious: 0.975, desktopNext: 0.95, mobilePrevious: 0.985, mobileNext: 0.975 },
      standard: { desktopPrevious: 0.96, desktopNext: 0.93, mobilePrevious: 0.97, mobileNext: 0.96 },
      strong: { desktopPrevious: 0.94, desktopNext: 0.9, mobilePrevious: 0.965, mobileNext: 0.95 },
    };

    return {
      enabled: asBoolean(configNode.dataset.enabled),
      desktop: asBoolean(configNode.dataset.desktopEnabled),
      mobile: asBoolean(configNode.dataset.mobileEnabled),
      range: ranges[strength] || ranges.standard,
    };
  };

  const resetSection = (section) => {
    section.style.setProperty("--cp-scale", "1");
    section.style.setProperty("--cp-opacity", "1");
    section.classList.remove("cp-will-change");
  };

  const cacheGeometry = () => {
    geometry = sections.map((section) => {
      const rect = section.getBoundingClientRect();
      return { top: rect.top + window.scrollY, height: rect.height };
    });
    recacheRequested = false;
  };

  const updateTransitions = () => {
    frame = 0;
    if (!dirty) return;
    dirty = false;

    if (recacheRequested) cacheGeometry();

    const settings = motionSettings();
    const isMobile = mobileViewport.matches;
    const enabledForViewport = isMobile ? settings.mobile : settings.desktop;

    sections.forEach(resetSection);

    if (!settings.enabled || !enabledForViewport || reducedMotion.matches || liteMode) return;

    const viewportHeight = window.innerHeight;
    const start = viewportHeight * 0.92;
    const end = viewportHeight * 0.28;
    const range = isMobile ? settings.range.mobileNext : settings.range.desktopNext;
    const previousMinimum = isMobile ? settings.range.mobilePrevious : settings.range.desktopPrevious;

    for (let index = 0; index < sections.length - 1; index += 1) {
      const nextTop = geometry[index + 1].top - window.scrollY;
      if (nextTop > start || nextTop < end) continue;

      const progress = clamp((start - nextTop) / (start - end));
      const current = sections[index];
      const next = sections[index + 1];

      if (current.dataset.cpFrozen === undefined && !current.classList.contains("is-expanded")) {
        current.style.setProperty("--cp-scale", mix(1, previousMinimum, progress).toFixed(4));
        current.style.setProperty("--cp-opacity", mix(1, 0.97, progress).toFixed(4));
      }

      if (next.dataset.cpFrozen === undefined && !next.classList.contains("is-expanded")) {
        next.style.setProperty("--cp-scale", mix(range, 1, progress).toFixed(4));
        next.style.setProperty("--cp-opacity", mix(0.97, 1, progress).toFixed(4));
      }

      current.classList.add("cp-will-change");
      next.classList.add("cp-will-change");
    }
  };

  const requestUpdate = (recache = false) => {
    dirty = true;
    recacheRequested ||= recache;
    if (!frame) frame = window.requestAnimationFrame(updateTransitions);
  };

  const initializeHero = () => {
    const hero = document.querySelector(".template-page-creator-program .cp-hero");
    if (!hero || hero.dataset.cpHeroAnimated !== undefined) return;

    hero.dataset.cpHeroAnimated = "";
    const lines = Array.from(hero.querySelectorAll("[data-cp-hero-line]"));
    const copy = hero.querySelector("[data-cp-hero-copy]");
    const button = hero.querySelector("[data-cp-hero-button]");
    const media = hero.querySelector("[data-cp-hero-media]");

    if (reducedMotion.matches || liteMode || !("animate" in Element.prototype)) return;

    const play = () => {
      const ease = "cubic-bezier(.215, .61, .355, 1)";
      lines.forEach((line, index) => {
        line.animate(
          [
            { opacity: 0, transform: "translate3d(0, 45px, 0)" },
            { opacity: 1, transform: "translate3d(0, 0, 0)" },
          ],
          { duration: 800, delay: index * 120, easing: ease, fill: "both" },
        );
      });

      copy?.animate(
        [
          { opacity: 0, transform: "translate3d(0, 20px, 0)" },
          { opacity: 1, transform: "translate3d(0, 0, 0)" },
        ],
        { duration: 700, delay: 350, easing: ease, fill: "both" },
      );

      button?.animate(
        [
          { opacity: 0, transform: "translate3d(0, 15px, 0) scale(.96)" },
          { opacity: 1, transform: "translate3d(0, 0, 0) scale(1)" },
        ],
        { duration: 650, delay: 560, easing: ease, fill: "both" },
      );

      media?.animate(
        [
          { opacity: 0, transform: "translate3d(30px, 0, 0) scale(.96)" },
          { opacity: 1, transform: "translate3d(0, 0, 0) scale(1)" },
        ],
        { duration: 1000, delay: 80, easing: ease, fill: "both" },
      );
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        document.fonts?.ready.then(play) || play();
      },
      { threshold: 0.1, rootMargin: "0px 0px -100px" },
    );

    observer.observe(hero);
  };

  const bindHeroFormScroll = () => {
    const button = document.querySelector(
      '.template-page-creator-program [data-cp-hero-button][href="#creator-program-form"]',
    );
    const form = document.getElementById("creator-program-form");
    if (!button || !form || button.dataset.cpFormScrollBound === "true") return;

    button.dataset.cpFormScrollBound = "true";
    button.addEventListener("click", (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();
      form.scrollIntoView({
        behavior: reducedMotion.matches || liteMode ? "auto" : "smooth",
        block: "start",
      });

      if (window.location.hash !== "#creator-program-form") {
        window.history.pushState(null, "", "#creator-program-form");
      }
    });
  };

  const splitTitleIntoLines = (title) => {
    if (!title || title.dataset.cpRevealSplit === "lines") {
      return Array.from(title?.querySelectorAll?.(".cp-reveal-line") || []);
    }

    const source = title.cloneNode(true);
    source.querySelectorAll("br").forEach((breakNode) => breakNode.replaceWith("\n"));
    const explicitLines = source.textContent
      .split("\n")
      .map((line) => line.trim().replace(/\s+/g, " "))
      .filter(Boolean);
    const text = explicitLines.join(" ");
    if (!text) return [];

    title.dataset.cpRevealSplit = "lines";
    title.setAttribute("aria-label", text);
    if (explicitLines.length > 1) {
      const visual = document.createElement("span");
      visual.setAttribute("aria-hidden", "true");
      explicitLines.forEach((lineText) => {
        const clip = document.createElement("span");
        const line = document.createElement("span");
        clip.className = "cp-reveal-line-clip";
        line.className = "cp-reveal-line";
        line.textContent = lineText;
        clip.append(line);
        visual.append(clip);
      });
      title.replaceChildren(visual);
      return Array.from(title.querySelectorAll(".cp-reveal-line"));
    }

    const measure = document.createElement("span");
    measure.setAttribute("aria-hidden", "true");
    measure.className = "cp-reveal-measure";

    text.split(" ").forEach((word, index) => {
      const wordNode = document.createElement("span");
      wordNode.textContent = `${word}${index < text.split(" ").length - 1 ? " " : ""}`;
      measure.append(wordNode);
    });

    title.replaceChildren(measure);
    const groups = [];
    Array.from(measure.children).forEach((wordNode) => {
      const top = Math.round(wordNode.offsetTop);
      const current = groups.at(-1);
      if (!current || current.top !== top) groups.push({ top, words: [wordNode.textContent] });
      else current.words.push(wordNode.textContent);
    });

    const visual = document.createElement("span");
    visual.setAttribute("aria-hidden", "true");
    groups.forEach((group) => {
      const clip = document.createElement("span");
      const line = document.createElement("span");
      clip.className = "cp-reveal-line-clip";
      line.className = "cp-reveal-line";
      line.textContent = group.words.join("").trim();
      clip.append(line);
      visual.append(clip);
    });
    title.replaceChildren(visual);
    return Array.from(title.querySelectorAll(".cp-reveal-line"));
  };

  const splitTitleIntoWords = (title) => {
    if (!title || title.dataset.cpRevealSplit === "words") {
      return Array.from(title?.querySelectorAll?.(".cp-reveal-word") || []);
    }

    const text = title.textContent.trim().replace(/\s+/g, " ");
    if (!text) return [];
    title.dataset.cpRevealSplit = "words";
    title.setAttribute("aria-label", text);
    const visual = document.createElement("span");
    visual.setAttribute("aria-hidden", "true");
    text.split(" ").forEach((word, index, words) => {
      const node = document.createElement("span");
      node.className = "cp-reveal-word";
      node.textContent = word;
      visual.append(node);
      if (index < words.length - 1) visual.append(document.createTextNode(" "));
    });
    title.replaceChildren(visual);
    return Array.from(title.querySelectorAll(".cp-reveal-word"));
  };

  const reveal = (element, keyframes, options) => {
    if (!element) return;
    element.animate(keyframes, {
      duration: 600,
      easing: "cubic-bezier(.215, .61, .355, 1)",
      fill: "both",
      ...options,
    });
  };

  const initializeSectionReveals = () => {
    if (reducedMotion.matches || liteMode || !("animate" in Element.prototype)) return;

    const sectionConfigs = [
      { selector: ".cp-who", items: ".cp-who__qualification, .cp-who__media", itemDelay: 70 },
      { selector: ".cp-benefits", items: ".cp-benefit-card", itemDelay: 120, itemY: 34, itemScale: 0.985 },
      { selector: ".cp-steps", items: ".cp-step-card", itemDelay: 100, itemY: 28 },
      { selector: ".cp-community", items: ".cp-community__media, .cp-community__phone-frame", itemDelay: 18, itemY: 24, itemScale: 1, itemDuration: 520 },
      { selector: ".cp-form", items: ".cp-form__app", itemDelay: 0 },
      { selector: ".cp-faq", items: ".cp-faq__item", itemDelay: 70 },
    ];

    sectionConfigs.forEach((config) => {
      document.querySelectorAll(`.template-page-creator-program ${config.selector}`).forEach((section) => {
        if (section.dataset.cpRevealReady !== undefined) return;
        section.dataset.cpRevealReady = "";
        const title = section.querySelector(".cp-title");
        const titleLines = splitTitleIntoLines(title);
        const intro = section.querySelector(".cp-heading-group > .cp-copy");
        const items = Array.from(section.querySelectorAll(config.items));

        const play = () => {
          titleLines.forEach((line, index) => reveal(
            line,
            [{ opacity: 0, transform: "translate3d(0, 38px, 0)" }, { opacity: 1, transform: "translate3d(0, 0, 0)" }],
            { duration: 750, delay: index * 100 },
          ));
          reveal(intro, [{ opacity: 0, transform: "translate3d(0, 20px, 0)" }, { opacity: 1, transform: "translate3d(0, 0, 0)" }], { duration: 560, delay: 150 });
          items.forEach((item, index) => reveal(
            item,
            [
              { opacity: 0, transform: `translate3d(0, ${config.itemY || 20}px, 0) scale(${config.itemScale || 1})` },
              { opacity: 1, transform: "translate3d(0, 0, 0) scale(1)" },
            ],
            { duration: config.itemDuration || 600, delay: 250 + index * config.itemDelay },
          ));
        };

        const observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          play();
        }, { threshold: 0.15, rootMargin: "0px 0px -60px" });
        observer.observe(section);
      });
    });

    document.querySelectorAll(".template-page-creator-program .cp-cta").forEach((section) => {
      if (section.dataset.cpRevealReady !== undefined) return;
      section.dataset.cpRevealReady = "";
      const words = splitTitleIntoWords(section.querySelector(".cp-title"));
      const copy = section.querySelector(".cp-copy");
      const button = section.querySelector(".cp-button");
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        words.forEach((word, index) => reveal(word, [{ opacity: 0, transform: "translate3d(0, 28px, 0)" }, { opacity: 1, transform: "translate3d(0, 0, 0)" }], { duration: 650, delay: index * 70 }));
        reveal(copy, [{ opacity: 0, transform: "translate3d(0, 20px, 0)" }, { opacity: 1, transform: "translate3d(0, 0, 0)" }], { duration: 560, delay: 230 });
        reveal(button, [{ opacity: 0, transform: "translate3d(0, 12px, 0) scale(.96)" }, { opacity: 1, transform: "translate3d(0, 0, 0) scale(1)" }], { duration: 560, delay: 400 });
      }, { threshold: 0.15, rootMargin: "0px 0px -60px" });
      observer.observe(section);
    });
  };

  class CommunityGallery {
    constructor(root) {
      this.root = root;
      this.items = Array.from(root.querySelectorAll("[data-cp-community-item]"));
      this.enabled = root.dataset.cpInteractive === "true";
      this.activeItem = null;
      this.onRootClick = this.onRootClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);

      this.root.addEventListener("click", this.onRootClick);
      document.addEventListener("click", this.onDocumentClick);
      document.addEventListener("keydown", this.onDocumentKeydown);
      document.addEventListener("shopify:block:select", this.onBlockSelect);
      document.addEventListener("shopify:block:deselect", this.onBlockDeselect);
    }

    setActive(item) {
      this.items.forEach((candidate) => {
        const isActive = candidate === item;
        candidate.classList.toggle("is-active", isActive);
        candidate.setAttribute("aria-pressed", String(isActive));
      });
      this.activeItem = item;
    }

    onRootClick(event) {
      const item = event.target.closest("[data-cp-community-item]");
      if (!this.enabled || !item || !this.root.contains(item)) return;
      this.setActive(this.activeItem === item ? null : item);
    }

    onDocumentClick(event) {
      if (!this.root.contains(event.target)) this.setActive(null);
    }

    onDocumentKeydown(event) {
      if (event.key === "Escape") this.setActive(null);
    }

    onBlockSelect(event) {
      if (!this.root.contains(event.target)) return;
      const item = event.target.matches?.("[data-cp-community-item]")
        ? event.target
        : event.target.querySelector?.("[data-cp-community-item]");
      if (item) this.setActive(item);
    }

    onBlockDeselect(event) {
      if (this.root.contains(event.target)) this.setActive(null);
    }

    destroy() {
      this.setActive(null);
      this.root.removeEventListener("click", this.onRootClick);
      document.removeEventListener("click", this.onDocumentClick);
      document.removeEventListener("keydown", this.onDocumentKeydown);
      document.removeEventListener("shopify:block:select", this.onBlockSelect);
      document.removeEventListener("shopify:block:deselect", this.onBlockDeselect);
    }
  }

  class BenefitCardPulse {
    constructor(root) {
      this.root = root;
      this.activeItem = null;
      this.frame = 0;
      this.onClick = this.onClick.bind(this);
      this.onAnimationEnd = this.onAnimationEnd.bind(this);

      this.root.addEventListener("click", this.onClick);
      this.root.addEventListener("animationend", this.onAnimationEnd);
    }

    onClick(event) {
      const card = event.target.closest(".cp-benefit-card");
      if (!card || !this.root.contains(card)) return;

      const item = card.closest(".cp-benefit-item");
      if (!item) return;

      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.activeItem?.classList.remove("is-click-pulsing");
      this.activeItem = item;

      this.frame = window.requestAnimationFrame(() => {
        if (this.activeItem === item) item.classList.add("is-click-pulsing");
        this.frame = 0;
      });
    }

    onAnimationEnd(event) {
      if (event.animationName !== "cp-benefit-card-click" || event.target !== this.activeItem) return;
      this.activeItem.classList.remove("is-click-pulsing");
      this.activeItem = null;
    }

    destroy() {
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.activeItem?.classList.remove("is-click-pulsing");
      this.root.removeEventListener("click", this.onClick);
      this.root.removeEventListener("animationend", this.onAnimationEnd);
    }
  }

  const bindFormFocus = (formSection) => {
    if (formSection.dataset.cpFocusBound === "true") return;
    formSection.dataset.cpFocusBound = "true";
    formSection.addEventListener("focusin", () => {
      formSection.dataset.cpFrozen = "";
      requestUpdate();
    });
    formSection.addEventListener("focusout", () => {
      window.setTimeout(() => {
        if (!formSection.contains(document.activeElement)) {
          delete formSection.dataset.cpFrozen;
          requestUpdate();
        }
      }, 0);
    });
  };

  const hasRenderedEmbeddedForm = (app) => {
    const hosts = Array.from(app.querySelectorAll("shopify-forms-embed"));
    return hosts.some((host) => {
      if (host.getBoundingClientRect().height > 160) return true;
      const shadow = host.shadowRoot;
      return Boolean(shadow?.querySelector("form, input, textarea, select, button"));
    });
  };

  const bindEmbeddedFormState = (formSection) => {
    const app = formSection.querySelector("[data-cp-form-app]");
    if (!app || app.dataset.cpFormStateBound === "true") return;

    app.dataset.cpFormStateBound = "true";
    app.dataset.cpFormReady = "false";
    let checks = 0;

    const check = () => {
      const ready = hasRenderedEmbeddedForm(app);
      app.dataset.cpFormReady = ready ? "true" : "false";

      if (ready) {
        delete app.dataset.cpFormDelayed;
        formReadyTimers.delete(app);
        requestUpdate(true);
        return;
      }

      checks += 1;
      if (checks >= 16) {
        app.dataset.cpFormDelayed = "true";
        requestUpdate(true);
      }

      if (checks < 90) {
        const delay = checks < 24 ? 500 : 2000;
        formReadyTimers.set(app, window.setTimeout(check, delay));
      }
    };

    formReadyTimers.set(app, window.setTimeout(check, 100));
  };

  const initialize = () => {
    initializeHero();
    bindHeroFormScroll();
    document.fonts?.ready.then(initializeSectionReveals) || initializeSectionReveals();
    galleries.forEach((gallery) => gallery.destroy());
    galleries = Array.from(document.querySelectorAll("[data-cp-community]")).map(
      (root) => new CommunityGallery(root),
    );
    benefitCardPulses.forEach((pulse) => pulse.destroy());
    benefitCardPulses = Array.from(document.querySelectorAll("[data-cp-benefits]")).map(
      (root) => new BenefitCardPulse(root),
    );
    sections = Array.from(document.querySelectorAll("[data-cp-transition]"));
    document.querySelectorAll("[data-cp-form]").forEach((formSection) => {
      bindFormFocus(formSection);
      bindEmbeddedFormState(formSection);
    });
    resizeObserver?.disconnect();
    resizeObserver = new ResizeObserver(() => requestUpdate(true));
    sections.forEach((section) => resizeObserver.observe(section));
    document.querySelectorAll("[data-creator-program-page] img").forEach((image) => {
      if (!image.complete) image.addEventListener("load", () => requestUpdate(true), { once: true });
    });
    requestUpdate(true);
  };

  window.addEventListener("scroll", () => requestUpdate(), { passive: true });
  window.addEventListener("resize", () => {
    requestUpdate(true);
  }, { passive: true });
  reducedMotion.addEventListener?.("change", () => requestUpdate(true));
  mobileViewport.addEventListener?.("change", () => requestUpdate(true));
  document.fonts?.ready.then(() => requestUpdate(true));

  ["shopify:section:load", "shopify:section:unload", "shopify:section:reorder"].forEach((eventName) => {
    document.addEventListener(eventName, () => window.setTimeout(initialize, 0));
  });

  document.addEventListener("shopify:section:select", (event) => {
    const selected = event.target.querySelector?.("[data-cp-transition]") || event.target.closest?.("[data-cp-transition]");
    if (selected) selected.dataset.cpFrozen = "";
    requestUpdate(true);
  });

  document.addEventListener("shopify:section:deselect", (event) => {
    const selected = event.target.querySelector?.("[data-cp-transition]") || event.target.closest?.("[data-cp-transition]");
    if (selected) delete selected.dataset.cpFrozen;
    requestUpdate(true);
  });

  initialize();
})();
